<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <nav class="breadcrumb-nav mb-2">
                    <a href="/tokens" class="text-muted">Tokens</a>
                    <span class="mx-2 text-muted">/</span>
                    <a v-if="baseToken?.mint" :href="`/tokens/${baseToken.mint}`" class="text-muted">{{ baseToken.symbol || '?' }}</a>
                    <span v-if="baseToken?.mint" class="mx-2 text-muted">/</span>
                    <span class="text-gradient">{{ pool ? (pool.token_a?.symbol + ' / ' + pool.token_b?.symbol + ' Pool') : 'Pool Detail' }}</span>
                </nav>

                <!-- Pool Header -->
                <div class="pool-header-row" v-if="pool">
                    <div class="pool-pair-info">
                        <div class="pair-logos">
                            <img v-if="pool.token_a?.image" :src="pool.token_a.image" :alt="pool.token_a.symbol" class="token-logo token-logo--a">
                            <div v-else class="token-logo token-logo--a token-logo--placeholder">{{ (pool.token_a?.symbol || '?')[0] }}</div>
                            <img v-if="pool.token_b?.image" :src="pool.token_b.image" :alt="pool.token_b.symbol" class="token-logo token-logo--b">
                            <div v-else class="token-logo token-logo--b token-logo--placeholder">{{ (pool.token_b?.symbol || '?')[0] }}</div>
                        </div>
                        <div>
                            <h1 class="pool-title mb-1">
                                <span class="text-gradient">{{ pool.token_a?.symbol || '?' }} / {{ pool.token_b?.symbol || '?' }}</span>
                            </h1>
                            <div class="pool-badges">
                                <span class="badge badge-protocol" :class="'badge-protocol--' + protocolColor">{{ protocolDisplayName }}</span>
                                <span class="badge badge-pool-type">{{ (pool.pool_type || 'AMM').toUpperCase() }}</span>
                                <span class="badge badge-fee" v-if="pool.fee_tier_bps != null">{{ pool.fee_tier_percent }}% fee</span>
                            </div>
                        </div>
                    </div>
                    <div class="pool-address-row">
                        <a :href="`https://solscan.io/account/${address}`" target="_blank" class="text-gradient font-mono text-sm">
                            {{ address }}
                        </a>
                        <button class="btn-copy" @click="copyAddress" :title="copied ? 'Copied!' : 'Copy address'">
                            <svg v-if="!copied" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                        </button>
                    </div>
                </div>

                <date-range-picker default-range="7d" @change="handleDateRangeChange" />
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-muted mt-2">Loading pool data...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="alert alert-danger">
            {{ error }}
        </div>

        <template v-if="pool && !loading">
            <!-- Hero Stats -->
            <div class="analytics-hero mb-4">
                <div class="analytics-hero-stat">
                    <div class="hero-label">TVL</div>
                    <div class="hero-value">${{ formatCompact(pool.tvl_usd) }}</div>
                </div>
                <div class="analytics-hero-stat hero-stat--green">
                    <div class="hero-label">24h Volume</div>
                    <div class="hero-value">${{ formatCompact(pool.stats_24h?.volume_usd) }}</div>
                </div>
                <div class="analytics-hero-stat hero-stat--cyan">
                    <div class="hero-label">24h Fees</div>
                    <div class="hero-value">${{ formatCompact(estimatedFees24h) }}</div>
                    <div class="hero-subtitle">est. from volume</div>
                </div>
                <div class="analytics-hero-stat hero-stat--blue">
                    <div class="hero-label">24h Trades</div>
                    <div class="hero-value">{{ formatCompact(pool.stats_24h?.trade_count) }}</div>
                </div>
                <div class="analytics-hero-stat hero-stat--amber">
                    <div class="hero-label">24h Traders</div>
                    <div class="hero-value">{{ formatCompact(pool.stats_24h?.traders_unique) }}</div>
                </div>
                <div class="analytics-hero-stat">
                    <div class="hero-label">Vol/Liq</div>
                    <div class="hero-value">{{ volLiqRatio }}</div>
                </div>
            </div>

            <!-- Buy/Sell Pressure Bar -->
            <div class="buy-sell-bar-container mb-4" v-if="pool.stats_24h?.buy_volume_usd || pool.stats_24h?.sell_volume_usd">
                <div class="buy-sell-header">
                    <span class="buy-label">Buy ${{ formatCompact(pool.stats_24h.buy_volume_usd) }}</span>
                    <span class="sell-label">Sell ${{ formatCompact(pool.stats_24h.sell_volume_usd) }}</span>
                </div>
                <div class="buy-sell-bar">
                    <div class="buy-fill" :style="{ width: buyPercent + '%' }"></div>
                    <div class="sell-fill" :style="{ width: (100 - buyPercent) + '%' }"></div>
                </div>
            </div>

            <!-- SECTION: Volume Charts -->
            <div class="section-divider">
                <span class="section-title">Volume & Activity</span>
            </div>
            <div class="analytics-row cols-2 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Trading Volume</div>
                        <div class="panel-subtitle">Buy vs Sell volume over time</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="volumeChart"></canvas>
                    </div>
                </div>
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Trade Count & Traders</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="tradesChart"></canvas>
                    </div>
                </div>
            </div>

            <!-- SECTION: Pool Reserves -->
            <div class="section-divider">
                <span class="section-title">Pool Reserves</span>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-body">
                        <div class="reserves-grid">
                            <a :href="`/tokens/${pool.token_a?.mint}`" class="reserve-card reserve-card--link" v-if="pool.token_a?.mint">
                                <div class="reserve-header">
                                    <img v-if="pool.token_a?.image" :src="pool.token_a.image" class="reserve-logo" :alt="pool.token_a.symbol">
                                    <div v-else class="reserve-logo reserve-logo--placeholder">{{ (pool.token_a?.symbol || '?')[0] }}</div>
                                    <div class="reserve-header-text">
                                        <div class="reserve-symbol">{{ pool.token_a?.symbol || '?' }}</div>
                                        <div class="reserve-name text-muted">{{ pool.token_a?.name || '' }}</div>
                                    </div>
                                    <div class="reserve-price mono-num" v-if="pool.token_a?.price_usd">${{ formatPrice(pool.token_a.price_usd) }}</div>
                                </div>
                                <div class="reserve-details">
                                    <div class="reserve-detail-row">
                                        <span class="reserve-detail-label">Reserves</span>
                                        <span class="reserve-detail-value mono-num">{{ formatTokenAmount(pool.token_a?.reserve) }} {{ pool.token_a?.symbol }}</span>
                                    </div>
                                    <div class="reserve-detail-row">
                                        <span class="reserve-detail-label">Value</span>
                                        <span class="reserve-detail-value mono-num">${{ formatCompact(pool.token_a?.reserve_usd) }}</span>
                                    </div>
                                    <div class="reserve-detail-row">
                                        <span class="reserve-detail-label">Share</span>
                                        <span class="reserve-detail-value mono-num">{{ reservePercentA }}% of TVL</span>
                                    </div>
                                </div>
                            </a>
                            <a :href="`/tokens/${pool.token_b?.mint}`" class="reserve-card reserve-card--link" v-if="pool.token_b?.mint">
                                <div class="reserve-header">
                                    <img v-if="pool.token_b?.image" :src="pool.token_b.image" class="reserve-logo" :alt="pool.token_b.symbol">
                                    <div v-else class="reserve-logo reserve-logo--placeholder">{{ (pool.token_b?.symbol || '?')[0] }}</div>
                                    <div class="reserve-header-text">
                                        <div class="reserve-symbol">{{ pool.token_b?.symbol || '?' }}</div>
                                        <div class="reserve-name text-muted">{{ pool.token_b?.name || '' }}</div>
                                    </div>
                                    <div class="reserve-price mono-num" v-if="pool.token_b?.price_usd">${{ formatPrice(pool.token_b.price_usd) }}</div>
                                </div>
                                <div class="reserve-details">
                                    <div class="reserve-detail-row">
                                        <span class="reserve-detail-label">Reserves</span>
                                        <span class="reserve-detail-value mono-num">{{ formatTokenAmount(pool.token_b?.reserve) }} {{ pool.token_b?.symbol }}</span>
                                    </div>
                                    <div class="reserve-detail-row">
                                        <span class="reserve-detail-label">Value</span>
                                        <span class="reserve-detail-value mono-num">${{ formatCompact(pool.token_b?.reserve_usd) }}</span>
                                    </div>
                                    <div class="reserve-detail-row">
                                        <span class="reserve-detail-label">Share</span>
                                        <span class="reserve-detail-value mono-num">{{ reservePercentB }}% of TVL</span>
                                    </div>
                                </div>
                            </a>
                        </div>

                        <!-- Reserve ratio bar -->
                        <div class="ratio-bar mt-3">
                            <div class="ratio-fill ratio-fill--a" :style="{ width: reservePercentA + '%' }"></div>
                            <div class="ratio-fill ratio-fill--b" :style="{ width: reservePercentB + '%' }"></div>
                        </div>

                        <!-- CLMM tick info -->
                        <div class="clmm-info mt-3" v-if="pool.tick_current != null">
                            <div class="clmm-stat">
                                <span class="text-muted">Current Tick:</span>
                                <span class="mono-num">{{ pool.tick_current }}</span>
                            </div>
                            <div class="clmm-stat" v-if="pool.tick_spacing">
                                <span class="text-muted">Tick Spacing:</span>
                                <span class="mono-num">{{ pool.tick_spacing }}</span>
                            </div>
                        </div>

                        <!-- External links -->
                        <div class="external-links mt-3">
                            <a :href="nativePlatformUrl" target="_blank" class="btn-external" v-if="nativePlatformUrl">
                                View on {{ protocolDisplayName }}
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3.5 3C3.22386 3 3 3.22386 3 3.5V8.5C3 8.77614 3.22386 9 3.5 9H8.5C8.77614 9 9 8.77614 9 8.5V6.5M6.5 3H9M9 3V5.5M9 3L5 7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </a>
                            <a :href="`https://solscan.io/account/${address}`" target="_blank" class="btn-external">
                                Solscan
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3.5 3C3.22386 3 3 3.22386 3 3.5V8.5C3 8.77614 3.22386 9 3.5 9H8.5C8.77614 9 9 8.77614 9 8.5V6.5M6.5 3H9M9 3V5.5M9 3L5 7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <!-- SECTION: Recent Trades -->
            <div class="section-divider">
                <span class="section-title">Recent Trades</span>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-body p-0">
                        <div class="table-responsive">
                            <table class="table compass-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Type</th>
                                        <th class="text-end">{{ pool.token_a?.symbol || 'Token A' }}</th>
                                        <th class="text-end">{{ pool.token_b?.symbol || 'Token B' }}</th>
                                        <th class="text-end">Value</th>
                                        <th>Wallet</th>
                                        <th>Tx</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="!pool.recent_trades || pool.recent_trades.length === 0">
                                        <td colspan="7" class="text-center text-muted py-4">No recent trades found</td>
                                    </tr>
                                    <tr v-for="trade in pool.recent_trades" :key="trade.signature">
                                        <td class="text-muted text-sm">{{ formatTime(trade.time) }}</td>
                                        <td>
                                            <span class="trade-direction" :class="getTradeDirection(trade).class">
                                                {{ getTradeDirection(trade).label }}
                                            </span>
                                        </td>
                                        <td class="text-end mono-num">{{ formatTokenAmount(trade.token_a?.amount) }}</td>
                                        <td class="text-end mono-num">{{ formatTokenAmount(trade.token_b?.amount) }}</td>
                                        <td class="text-end mono-num">${{ formatCompact(trade.total_value_usd) }}</td>
                                        <td>
                                            <a v-if="trade.signer" :href="`https://solscan.io/account/${trade.signer}`" target="_blank" class="wallet-link font-mono">
                                                {{ formatPubkey(trade.signer) }}
                                            </a>
                                        </td>
                                        <td>
                                            <a v-if="trade.signature" :href="`https://solscan.io/tx/${trade.signature}`" target="_blank" class="tx-link" title="View transaction">
                                                <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M3.5 3C3.22386 3 3 3.22386 3 3.5V8.5C3 8.77614 3.22386 9 3.5 9H8.5C8.77614 9 9 8.77614 9 8.5V6.5M6.5 3H9M9 3V5.5M9 3L5 7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                            </a>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { formatCompact, formatPubkey } from '../composables/useFormatters';
import { colors, getLineChartOptions, getBarChartOptions, getStackedBarChartOptions } from '../composables/useChartConfig';

const protocolNames = {
    raydium_amm: { name: 'Raydium AMM', color: 'purple' },
    raydium_clmm: { name: 'Raydium CLMM', color: 'purple' },
    raydium_cpmm: { name: 'Raydium CPMM', color: 'purple' },
    orca_whirlpool: { name: 'Orca', color: 'cyan' },
    meteora_dlmm: { name: 'Meteora DLMM', color: 'orange' },
    meteora_pools: { name: 'Meteora', color: 'orange' },
    meteora_dammv2: { name: 'Meteora DAMM', color: 'orange' },
    lifinity: { name: 'Lifinity', color: 'blue' },
};

export default {
    name: 'PoolDetail',
    props: { address: { type: String, required: true } },

    data() {
        return {
            pool: null,
            loading: true,
            error: null,
            currentRange: '7d',
            currentInterval: '1h',
            charts: {},
            copied: false,
        };
    },

    computed: {
        baseToken() {
            if (!this.pool) return null;
            // Known quote tokens — if one side is a quote, the other is the base
            const quoteSymbols = ['SOL', 'USDC', 'USDT', 'WSOL', 'mSOL', 'jitoSOL', 'bSOL', 'USDH', 'UXD', 'PYUSD'];
            const aSymbol = this.pool.token_a?.symbol || '';
            const bSymbol = this.pool.token_b?.symbol || '';
            if (quoteSymbols.includes(bSymbol) && !quoteSymbols.includes(aSymbol)) return this.pool.token_a;
            if (quoteSymbols.includes(aSymbol) && !quoteSymbols.includes(bSymbol)) return this.pool.token_b;
            // Both or neither are quote — pick token_a as base
            return this.pool.token_a;
        },
        protocolDisplayName() {
            if (!this.pool) return '';
            return protocolNames[this.pool.protocol]?.name || this.pool.program_name || this.pool.protocol || 'Unknown';
        },
        protocolColor() {
            if (!this.pool) return 'gray';
            return protocolNames[this.pool.protocol]?.color || 'gray';
        },
        estimatedFees24h() {
            if (!this.pool?.stats_24h?.volume_usd || !this.pool?.fee_tier_bps) return 0;
            return (this.pool.stats_24h.volume_usd * this.pool.fee_tier_bps) / 10000;
        },
        volLiqRatio() {
            if (!this.pool?.tvl_usd || this.pool.tvl_usd === 0) return '0x';
            const ratio = (this.pool.stats_24h?.volume_usd || 0) / this.pool.tvl_usd;
            if (ratio >= 10) return ratio.toFixed(0) + 'x';
            if (ratio >= 1) return ratio.toFixed(1) + 'x';
            return ratio.toFixed(2) + 'x';
        },
        buyPercent() {
            const buy = this.pool?.stats_24h?.buy_volume_usd || 0;
            const sell = this.pool?.stats_24h?.sell_volume_usd || 0;
            const total = buy + sell;
            return total > 0 ? ((buy / total) * 100).toFixed(1) : 50;
        },
        reservePercentA() {
            const a = this.pool?.token_a?.reserve_usd || 0;
            const b = this.pool?.token_b?.reserve_usd || 0;
            const total = a + b;
            return total > 0 ? ((a / total) * 100).toFixed(1) : 50;
        },
        reservePercentB() {
            const a = this.pool?.token_a?.reserve_usd || 0;
            const b = this.pool?.token_b?.reserve_usd || 0;
            const total = a + b;
            return total > 0 ? ((b / total) * 100).toFixed(1) : 50;
        },
        nativePlatformUrl() {
            if (!this.pool?.protocol) return null;
            const addr = this.address;
            switch (this.pool.protocol) {
                case 'orca_whirlpool': return `https://www.orca.so/pools/${addr}`;
                case 'meteora_dlmm': return `https://app.meteora.ag/dlmm/${addr}`;
                case 'meteora_pools': return `https://app.meteora.ag/pools/${addr}`;
                case 'meteora_dammv2': return `https://app.meteora.ag/dammv2/${addr}`;
                case 'raydium_clmm': return `https://raydium.io/clmm/create-position/?pool_id=${addr}`;
                case 'raydium_amm':
                case 'raydium_cpmm': return `https://raydium.io/liquidity/increase/?mode=add&pool_id=${addr}`;
                default: return null;
            }
        },
    },

    mounted() {
        this.fetchData();
    },

    beforeUnmount() {
        this.destroyCharts();
    },

    methods: {
        formatCompact,
        formatPubkey,

        async fetchData() {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get(`/api/v1/pools/${this.address}`, {
                    params: { range: this.currentRange },
                });
                this.pool = response.data?.data || response.data;
                this.updatePageTitle();
                this.$nextTick(() => this.renderCharts());
            } catch (err) {
                if (err.response?.status === 404) {
                    this.error = 'Pool not found. It may have been closed or the address is incorrect.';
                } else {
                    this.error = 'Failed to load pool data. Please try again later.';
                }
                console.error('Pool detail fetch error:', err);
            } finally {
                this.loading = false;
            }
        },

        handleDateRangeChange({ range }) {
            this.currentRange = range;
            this.currentInterval = ['1h', '6h', '24h'].includes(range) ? '1h' : '1d';
            this.fetchData();
        },

        updatePageTitle() {
            if (!this.pool) return;
            const a = this.pool.token_a?.symbol || '?';
            const b = this.pool.token_b?.symbol || '?';
            const proto = this.protocolDisplayName;
            const poolType = (this.pool.pool_type || 'AMM').toUpperCase();
            document.title = `${a}/${b} ${proto} ${poolType} Pool Analytics | Solana Compass`;
            // Update meta description too
            const meta = document.querySelector('meta[name="description"]');
            if (meta) {
                meta.setAttribute('content', `${a}/${b} liquidity pool analytics on ${proto} — TVL, volume, trades, and fee data for this ${poolType} pool on Solana.`);
            }
        },

        copyAddress() {
            navigator.clipboard.writeText(this.address);
            this.copied = true;
            setTimeout(() => { this.copied = false; }, 2000);
        },

        formatPrice(val) {
            if (!val) return '0';
            if (val >= 1) return val.toFixed(2);
            if (val >= 0.01) return val.toFixed(4);
            if (val >= 0.0001) return val.toFixed(6);
            return val.toFixed(8);
        },

        formatTokenAmount(val) {
            if (!val && val !== 0) return '-';
            if (val >= 1e9) return (val / 1e9).toFixed(2) + 'B';
            if (val >= 1e6) return (val / 1e6).toFixed(2) + 'M';
            if (val >= 1e3) return (val / 1e3).toFixed(1) + 'K';
            if (val >= 1) return val.toFixed(2);
            if (val >= 0.001) return val.toFixed(4);
            return val.toFixed(6);
        },

        formatTime(isoString) {
            if (!isoString) return '-';
            const d = new Date(isoString);
            const now = new Date();
            const diff = (now - d) / 1000;
            if (diff < 60) return Math.floor(diff) + 's ago';
            if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
            if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        },

        getTradeDirection(trade) {
            // Determine buy/sell by comparing token amounts and values
            // If token_a amount > 0 and token_b amount > 0, it's a swap
            // We'll mark based on which token increased in the pool (the one sold into the pool)
            const aVal = (trade.token_a?.amount || 0) * (trade.token_a?.price_usd || 0);
            const bVal = (trade.token_b?.amount || 0) * (trade.token_b?.price_usd || 0);
            if (aVal > bVal) {
                return { label: 'Sell ' + (this.pool?.token_a?.symbol || 'A'), class: 'trade-sell' };
            }
            return { label: 'Buy ' + (this.pool?.token_a?.symbol || 'A'), class: 'trade-buy' };
        },

        destroyCharts() {
            Object.values(this.charts).forEach(chart => {
                if (chart && typeof chart.destroy === 'function') chart.destroy();
            });
            this.charts = {};
        },

        renderCharts() {
            this.destroyCharts();

            const ts = this.pool?.volume_time_series || [];
            if (!ts.length) return;

            const labels = ts.map(d => d.label);

            // Volume chart (stacked bar: buy/sell)
            if (this.$refs.volumeChart) {
                this.charts.volume = markRaw(new Chart(this.$refs.volumeChart, {
                    type: 'bar',
                    data: {
                        labels,
                        datasets: [
                            {
                                label: 'Buy Volume',
                                data: ts.map(d => d.buy_volume_usd),
                                backgroundColor: 'rgba(34, 197, 94, 0.7)',
                                borderColor: '#22c55e',
                                borderWidth: 1,
                                stack: 'volume',
                            },
                            {
                                label: 'Sell Volume',
                                data: ts.map(d => d.sell_volume_usd),
                                backgroundColor: 'rgba(239, 68, 68, 0.7)',
                                borderColor: '#ef4444',
                                borderWidth: 1,
                                stack: 'volume',
                            },
                        ],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: {
                            legend: { display: true, labels: { color: '#9ca3af', boxWidth: 12 } },
                            tooltip: {
                                callbacks: {
                                    label: (ctx) => `${ctx.dataset.label}: $${formatCompact(ctx.raw)}`,
                                },
                            },
                        },
                        scales: {
                            x: { ticks: { color: '#9ca3af', maxTicksLimit: 12 }, grid: { color: 'rgba(255,255,255,0.05)' }, stacked: true },
                            y: { ticks: { color: '#9ca3af', callback: (v) => '$' + formatCompact(v) }, grid: { color: 'rgba(255,255,255,0.05)' }, stacked: true },
                        },
                    },
                }));
            }

            // Trades + Traders chart
            if (this.$refs.tradesChart) {
                this.charts.trades = markRaw(new Chart(this.$refs.tradesChart, {
                    type: 'bar',
                    data: {
                        labels,
                        datasets: [
                            {
                                label: 'Trades',
                                data: ts.map(d => d.trade_count),
                                backgroundColor: 'rgba(139, 92, 246, 0.6)',
                                borderColor: '#8b5cf6',
                                borderWidth: 1,
                                yAxisID: 'y',
                            },
                            {
                                label: 'Unique Traders',
                                data: ts.map(d => d.traders),
                                type: 'line',
                                borderColor: '#10f7cf',
                                backgroundColor: 'rgba(16, 247, 207, 0.1)',
                                fill: true,
                                tension: 0.3,
                                pointRadius: 0,
                                yAxisID: 'y1',
                            },
                        ],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: {
                            legend: { display: true, labels: { color: '#9ca3af', boxWidth: 12 } },
                        },
                        scales: {
                            x: { ticks: { color: '#9ca3af', maxTicksLimit: 12 }, grid: { color: 'rgba(255,255,255,0.05)' } },
                            y: { position: 'left', ticks: { color: '#9ca3af', callback: (v) => formatCompact(v) }, grid: { color: 'rgba(255,255,255,0.05)' } },
                            y1: { position: 'right', ticks: { color: '#10f7cf' }, grid: { display: false } },
                        },
                    },
                }));
            }
        },
    },
};
</script>

<style scoped>
/* Pool Header */
.pool-header-row {
    margin-bottom: 1rem;
}
.pool-pair-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.5rem;
}
.pair-logos {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}
.token-logo {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid var(--compass-border, rgba(139, 92, 246, 0.2));
}
.token-logo--b {
    margin-left: -10px;
}
.token-logo--placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--compass-gradient-subtle, rgba(139, 92, 246, 0.1));
    color: var(--compass-purple, #8b5cf6);
    font-weight: 700;
    font-size: 16px;
}
.pool-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
}
.pool-badges {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}
.badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
    border-radius: var(--compass-radius-sm, 8px);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.badge-protocol {
    background: rgba(139, 92, 246, 0.15);
    color: #a78bfa;
}
.badge-protocol--purple { background: rgba(139, 92, 246, 0.15); color: #a78bfa; }
.badge-protocol--cyan { background: rgba(16, 247, 207, 0.15); color: #10f7cf; }
.badge-protocol--orange { background: rgba(249, 115, 28, 0.15); color: #fb923c; }
.badge-protocol--blue { background: rgba(59, 130, 246, 0.15); color: #60a5fa; }
.badge-pool-type {
    background: rgba(236, 72, 153, 0.12);
    color: #f472b6;
}
.badge-fee {
    background: rgba(34, 197, 94, 0.12);
    color: #4ade80;
}
.pool-address-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}
.btn-copy {
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 2px;
    transition: color 0.2s;
}
.btn-copy:hover { color: var(--compass-purple, #8b5cf6); }
.text-sm { font-size: 0.85rem; }

/* Buy/Sell Bar */
.buy-sell-bar-container {
    background: rgba(139, 92, 246, 0.05);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    border-radius: var(--compass-radius-sm, 8px);
    padding: 0.75rem 1rem;
}
.buy-sell-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    font-size: 0.85rem;
    font-weight: 600;
}
.buy-label { color: #22c55e; }
.sell-label { color: #ef4444; }
.buy-sell-bar {
    display: flex;
    height: 8px;
    border-radius: 4px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
}
.buy-fill {
    background: linear-gradient(90deg, #22c55e, #4ade80);
    transition: width 0.5s ease;
}
.sell-fill {
    background: linear-gradient(90deg, #f87171, #ef4444);
    transition: width 0.5s ease;
}

/* Reserves */
.reserves-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}
.reserve-card {
    background: rgba(139, 92, 246, 0.05);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
    border-radius: var(--compass-radius-sm, 8px);
    padding: 1rem;
}
.reserve-card--link {
    display: block;
    text-decoration: none;
    color: inherit;
    transition: border-color 0.2s, transform 0.2s;
}
.reserve-card--link:hover {
    border-color: var(--compass-purple, #8b5cf6);
    transform: translateY(-1px);
    text-decoration: none;
    color: inherit;
}
.reserve-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}
.reserve-header-text {
    flex: 1;
    min-width: 0;
}
.reserve-price {
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--compass-text-primary, #e6edf3);
    white-space: nowrap;
}
.reserve-logo {
    width: 28px;
    height: 28px;
    border-radius: 50%;
}
.reserve-logo--placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(139, 92, 246, 0.15);
    color: #a78bfa;
    font-weight: 700;
    font-size: 12px;
}
.reserve-symbol { font-weight: 600; font-size: 0.95rem; }
.reserve-name { font-size: 0.75rem; }
.reserve-details {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}
.reserve-detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.reserve-detail-label {
    font-size: 0.75rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.reserve-detail-value {
    font-size: 0.85rem;
}

/* Ratio Bar */
.ratio-bar {
    display: flex;
    height: 6px;
    border-radius: 3px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
}
.ratio-fill--a {
    background: var(--compass-purple, #8b5cf6);
    transition: width 0.5s ease;
}
.ratio-fill--b {
    background: var(--compass-pink, #ec4899);
    transition: width 0.5s ease;
}

/* CLMM info */
.clmm-info {
    display: flex;
    gap: 1.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
}
.clmm-stat {
    display: flex;
    gap: 0.5rem;
    font-size: 0.85rem;
}

/* External links */
.external-links {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
}
.btn-external {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.75rem;
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    border-radius: var(--compass-radius-sm, 8px);
    color: #a78bfa;
    text-decoration: none;
    font-size: 0.8rem;
    font-weight: 500;
    transition: all 0.2s;
}
.btn-external:hover {
    background: rgba(139, 92, 246, 0.1);
    border-color: var(--compass-purple, #8b5cf6);
    color: #c4b5fd;
    text-decoration: none;
}

/* Trades table */
.trade-direction {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
}
.trade-buy {
    background: rgba(34, 197, 94, 0.15);
    color: #4ade80;
}
.trade-sell {
    background: rgba(239, 68, 68, 0.15);
    color: #f87171;
}
.wallet-link {
    color: #9ca3af;
    text-decoration: none;
    font-size: 0.8rem;
}
.wallet-link:hover { color: var(--compass-purple, #8b5cf6); }
.tx-link {
    color: #6b7280;
    transition: color 0.2s;
}
.tx-link:hover { color: var(--compass-purple, #8b5cf6); }

/* Responsive */
@media (max-width: 768px) {
    .pool-title { font-size: 1.2rem; }
    .token-logo { width: 32px; height: 32px; }
    .reserves-grid { grid-template-columns: 1fr; }
    .pool-badges { font-size: 0.65rem; }
    .clmm-info { flex-direction: column; gap: 0.5rem; }
}
</style>
